import type { ErrorsMapper } from "@fas/ui-framework";
import dayjs from "dayjs";
import contentDisposition from "content-disposition";
import isEqual from "lodash.isequal";
import { useTheme } from "@mui/styles";
import { useMediaQuery } from "@mui/material";
import type { ObjectSchema, ValidationErrorItem } from "joi";
import type React from "react";

export const getValidation =
  (schema: ObjectSchema) =>
    (value: object): ErrorsMapper => {
      const result = schema.validate(value, {
        abortEarly: false,
      });

      const errorDetails = result.error?.details ?? [];

      return errorDetails.reduce<ErrorsMapper>(
        (acc, item: ValidationErrorItem) => {
          const path = item.path.join(".");
          acc[path] = {
            message: item.message,
          };
          return acc;
        },
        {}
      );
    };

const downloadData = ({ url, name }: { url: string; name: string }) => {
  const hiddenElement: HTMLAnchorElement = document.createElement("a");
  hiddenElement.href = url;
  hiddenElement.target = "_blank";
  hiddenElement.download = name;
  document.body && document.body.appendChild(hiddenElement); // for FF

  hiddenElement.click();
  hiddenElement.remove();
};

export function downloadCsv(data: string, name?: string) {
  downloadData({
    url: `data:text/csv;charset=utf-8,${encodeURI(data)}`,
    name: name || `Report-${dayjs().format("dddd_MMMM_Do_YYYY_H_mm_ss")}.csv`,
  });
}
export function downloadPdf(blob: Blob, name?: string) {
  downloadData({
    url: window.URL.createObjectURL(
      new Blob([blob], { type: "application/octet-stream" })
    ),
    name: name || `Invoice-${dayjs().format("dddd_MMMM_Do_YYYY_H_mm_ss")}.pdf`,
  });
}
export function downloadZip(blob: Blob, name?: string) {
  downloadData({
    url: window.URL.createObjectURL(
      new Blob([blob], { type: "application/octet-stream" })
    ),
    name: name || `Invoice-${dayjs().format("dddd_MMMM_Do_YYYY_H_mm_ss")}.zip`,
  });
}
export function getFileNameFromHeaders(
  headers: Record<string, unknown> | null | undefined,
  defaultName = ""
): string {
  if (!headers) return defaultName;

  try {
    const disposition = String(
      headers["content-disposition"] ||
        headers["Content-Disposition"] ||
        headers["Content-disposition"] ||
        ""
    );
    return (
      contentDisposition.parse(disposition).parameters.filename || defaultName
    );
  }
  catch {
    return defaultName;
  }
}
export const isEqualPreviousBuilder = () => {
  let oldVal: any;
  return function isEqualPrevious(currentVal: any): boolean {
    const isEqualValue: boolean = isEqual(currentVal, oldVal);
    oldVal = currentVal;
    return isEqualValue;
  };
};
export const readObjFromFile = (file: Blob | File) => {
  const fileReader = new FileReader();
  return new Promise<object>((resolve) => {
    fileReader.onload = () => {
      resolve(JSON.parse(fileReader.result as string));
    };

    fileReader.readAsText(file);
  });
};
export function numberWithCommas(x: number | string): string {
  return Intl.NumberFormat("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(x || 0));
}

export function formatCurrency(
  currency: string,
  value: string | number
): string {
  const result: string = Intl.NumberFormat("en-US", {
    style: "currency",
    currency: currency || "USD",
  }).format(Number(value));
  return `${result.split("").splice(0, 1).join("")} ${result.split("").splice(1).join("")}`;
}
export function RenderMediaQueryComponent({
  children,
  mediaKey,
  method = "down",
}: {
  children: React.JSX.Element | React.JSX.Element[];
  mediaKey: "xs" | "sm" | "md" | "lg";
  method: "up" | "down" | "only" | "not";
}) {
  const theme = useTheme();
  const isMatch = useMediaQuery(theme.breakpoints[method](mediaKey));
  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      {isMatch ? children : null}
    </>
  );
}
export const onKeyWrapper =
  (keys: string | string[]) => (fn: (arg0: any) => unknown) => {
    const keyList: string[] = Array.isArray(keys) ? keys : [keys];

    return function wrapped(...args: any) {
      const [{ key }]: any = args;

      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      if (keyList.includes(key)) fn.apply(this, args);
    };
  };
